/**
 * @file Detail-view notes section rendering and DOM wiring.
 */

import { appData, appDataDetails, state } from '../../app/state/state.js';
import {
    addPendingDetailNote,
    clearDetailNoteDraft,
    getDetailNoteDraft,
    getPendingDetailNotes,
    reconcilePendingDetailNotes,
    removePendingDetailNote,
    setDetailNoteDraft,
} from './noteDraftState.js';
import { initTagging } from '../ui/tagging.js';
import {
    AUTO_GROWING_TEXTAREA_CLASSES,
    bindNativeFieldBehavior,
} from '../forms/nativeFieldStyles.js';
import {
    escapeHTML,
    formatContentForSave,
    formatAndSanitizeUserContent,
    sanitizeHTML,
} from "../utils/contentUtils.js";
import {
    getCollectionName,
    getItemType,
} from "../utils/itemLookup.js";

function getCurrentUserName() {
    return state.currentUserProfile?.name ||
        state.currentUserProfile?.email ||
        state.currentUser?.displayName ||
        state.currentUser?.email ||
        'Unknown User';
}

function getUserDisplayName(user, fallback = 'Unknown User') {
    return user?.name || user?.email || fallback;
}

function getNoteDateLabel(note) {
    return note.createdAt?.toDate
        ? new Date(note.createdAt.toDate()).toLocaleString('en-NZ')
        : new Date(note.createdAtMs || Date.now()).toLocaleString('en-NZ');
}

function getSystemNoteActorName(note, users) {
    const actorUserId = String(note?.systemNote?.actorUserId || '').trim();
    if (!actorUserId) {return 'Unknown User';}
    if (actorUserId === 'system') {return 'System';}

    const actor = users.find(u => u.id === actorUserId);
    return getUserDisplayName(actor);
}

function formatSystemNoteTimestampText(content) {
    return String(content || '').replace(
        /\bon (\d{2}\/\d{2}\/\d{4}) at ([01]?\d|2[0-3]):([0-5]\d)\b/g,
        (_match, date, hourValue, minute) => {
            const hour = Number(hourValue);
            const period = hour >= 12 ? 'pm' : 'am';
            const displayHour = hour % 12 || 12;
            return `on ${date} at ${displayHour}:${minute} ${period}`;
        },
    );
}

function updateNotesOverflowIndicator(notesSection) {
    const notesList = notesSection?.querySelector?.('.notes-list');
    const indicator = notesSection?.querySelector?.('.notes-overflow-indicator');
    if (!notesList || !indicator) {return;}

    const isOverflowing = notesList.scrollHeight > notesList.clientHeight;
    const isScrolledToBottom = Math.abs(
        notesList.scrollHeight - notesList.scrollTop - notesList.clientHeight,
    ) < 10;

    indicator.classList.toggle('opacity-0', !isOverflowing || isScrolledToBottom);
}

/**
 * Renders the Notes section within the details view.
 * @param {object} item The item object used for its id.
 * @returns {string} Notes section HTML.
 */
export function renderNotesSection(item) {
    if (!item || !item.id) {return '';}

    const notes = Array.isArray(appDataDetails.notes)
        ? [...appDataDetails.notes].sort((a, b) =>
            new Date(b.createdAt?.toDate()) - new Date(a.createdAt?.toDate()))
        : [];
    reconcilePendingDetailNotes(item.id, notes);
    const pendingNotes = getPendingDetailNotes(item.id);
    const users = appData?.users || [];
    const noteDraft = getDetailNoteDraft(item.id);
    const noteActionsClass = noteDraft.hasText
        ? 'mt-2 flex items-center justify-end gap-2'
        : 'mt-2 hidden items-center justify-end gap-2';

    const renderNote = (note, { isPending = false } = {}) => {
        const isSystemNote = note?.isSystemNote === true;
        const user = users.find(u => u.id === note.createdBy);
        const userName = getUserDisplayName(user);
        const noteDate = getNoteDateLabel(note);
        const noteText = isSystemNote
            ? formatSystemNoteTimestampText(note.content)
            : note.content;
        const noteTextHtml = formatAndSanitizeUserContent(noteText);

        if (isSystemNote) {
            const actorName = getSystemNoteActorName(note, users);
            return `<div class="rounded-md border border-gray-200 bg-gray-50/80 p-2.5 text-gray-500 dark:border-gray-600 dark:bg-gray-700/60 dark:text-gray-400">
                    <div class="text-sm italic text-gray-400 dark:text-gray-500">${noteTextHtml}</div>
                    <p class="mt-1.5 text-[11px] text-gray-400 dark:text-gray-500">System note generated by ${sanitizeHTML(actorName)} on ${sanitizeHTML(noteDate)}</p>
                </div> `;
        }

        return `<div class="bg-gray-50 p-2.5 rounded-md border ${isPending ? 'border-[var(--dfc-brand-primary)]/60' : ''} dark:bg-gray-700 dark:border-gray-600">
                    <div class="text-sm dark:text-gray-300">${noteTextHtml}</div>
                    <p class="text-[11px] text-gray-400 mt-1.5 dark:text-gray-500">By ${sanitizeHTML(userName)} on ${sanitizeHTML(noteDate)}${isPending ? ' - Saving...' : ''}</p>
                </div> `;
    };

    const notesHtml = [
        ...pendingNotes.map(note => renderNote(note, { isPending: true })),
        ...notes.map(note => renderNote(note)),
    ].join('');

    return `
        <div class="notes-section-container bg-white dark:bg-gray-800 p-5 rounded-lg shadow-md mb-5 mt-5">
            <div class="flex justify-between items-center mb-4">
                <h2 class="text-xl font-semibold dark:text-gray-100">Notes</h2>
            </div>
            <form id="add-note-form" data-item-id="${item.id}" class="mb-3 text-right">
                <textarea name="note-text" class="${AUTO_GROWING_TEXTAREA_CLASSES} min-h-[82px] text-left sm:min-h-[68px]" placeholder="+ Add a new note" required rows="3" data-auto-grow="true">${escapeHTML(noteDraft.text)}</textarea>
                <div id="note-action-row" class="${noteActionsClass}">
                    <button type="button" id="cancel-note-btn" class="inline-flex h-9 items-center justify-center rounded-md bg-gray-200 px-4 text-sm font-semibold text-gray-800 transition-colors hover:bg-gray-300 focus:outline-none">Cancel</button>
                    <button type="submit" id="save-note-btn" class="inline-flex h-9 min-w-[92px] items-center justify-center rounded-md bg-[var(--dfc-brand-primary)] px-4 text-sm font-semibold text-gray-900 transition-colors hover:bg-[var(--dfc-brand-primary-hover)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50">Save Note</button>
                </div>
            </form>
            <div class="relative">
                <div class="notes-list max-h-96 space-y-3 overflow-y-auto pr-1">
                    ${notesHtml}
                </div>
                <div class="notes-overflow-indicator absolute bottom-0 left-0 right-1 h-8 bg-gradient-to-t from-white/90 to-transparent dark:from-gray-800/90 flex justify-center items-end pb-1 pointer-events-none opacity-0 transition-opacity duration-200">
                    <span class="material-symbols-outlined text-[18px] leading-none text-gray-800 dark:text-gray-200">expand_more</span>
                </div>
            </div>
        </div> `;
}

/**
 * Attaches notes section listeners and mention tagging.
 * @param {HTMLElement} detailsViewContainer Detail view container.
 * @param {object} callbacks Listener callbacks.
 * @param {Function} callbacks.handleAddNote Existing note submit handler.
 */
export function attachNotesSectionListeners(
    detailsViewContainer,
    { handleAddNote },
) {
    const addNoteForm = detailsViewContainer.querySelector('#add-note-form');
    if (!addNoteForm) {return;}

    const notesSection = addNoteForm.closest('.notes-section-container');
    const notesList = notesSection?.querySelector('.notes-list');
    let noteTextArea = addNoteForm.querySelector('textarea[name="note-text"]');
    const saveNoteBtn = addNoteForm.querySelector('#save-note-btn');
    const cancelNoteBtn = addNoteForm.querySelector('#cancel-note-btn');
    const noteActionRow = addNoteForm.querySelector('#note-action-row');
    const parentId = addNoteForm.dataset.itemId;

    let parentType = getItemType(parentId);
    if (!parentType && appDataDetails.tasks.find(t => t.id === parentId)) {
        parentType = 'task';
    }

    if (noteTextArea && parentId && parentType) {
        const collectionName = getCollectionName(parentType);
        initTagging(noteTextArea, parentId, collectionName);
        noteTextArea = addNoteForm.querySelector('textarea[name="note-text"]');
    }
    bindNativeFieldBehavior(addNoteForm);
    requestAnimationFrame(() => updateNotesOverflowIndicator(notesSection));
    notesList?.addEventListener('scroll', () =>
        updateNotesOverflowIndicator(notesSection));

    const syncNoteActions = () => {
        if (!noteTextArea || !noteActionRow || !saveNoteBtn || !parentId) {return;}

        const hasText = noteTextArea.value.trim().length > 0;
        if (hasText) {
            setDetailNoteDraft(parentId, noteTextArea.value);
            noteActionRow.classList.remove('hidden');
            noteActionRow.classList.add('flex');
            saveNoteBtn.disabled = false;
            return;
        }

        clearDetailNoteDraft(parentId);
        noteActionRow.classList.add('hidden');
        noteActionRow.classList.remove('flex');
        saveNoteBtn.disabled = true;
    };

    addNoteForm.onsubmit = async (e) => {
        const submittedText = noteTextArea?.value || '';
        const trimmedText = submittedText.trim();
        const pendingNote = trimmedText
            ? addPendingDetailNote(
                parentId,
                formatContentForSave(trimmedText),
                state.currentUser?.uid || '',
            )
            : null;
        if (pendingNote && notesList) {
            notesList.insertAdjacentHTML('afterbegin',
                `<div class="bg-gray-50 p-2.5 rounded-md border border-[var(--dfc-brand-primary)]/60 dark:bg-gray-700 dark:border-gray-600" data-pending-note-id="${pendingNote.id}">
                    <div class="text-sm dark:text-gray-300">${formatAndSanitizeUserContent(pendingNote.content)}</div>
                    <p class="text-[11px] text-gray-400 mt-1.5 dark:text-gray-500">By ${sanitizeHTML(getCurrentUserName())} on ${sanitizeHTML(new Date(pendingNote.createdAtMs).toLocaleString('en-NZ'))} - Saving...</p>
                </div>`);
            updateNotesOverflowIndicator(notesSection);
        }
        const savePromise = handleAddNote(e);
        if (trimmedText.length > 0) {
            clearDetailNoteDraft(parentId);
            if (noteTextArea) {
                noteTextArea.value = '';
                noteTextArea.dispatchEvent(new Event('input', { bubbles: true }));
            }
            syncNoteActions();
        }

        const saved = await savePromise;
        if (saved) {
            clearDetailNoteDraft(parentId);
            syncNoteActions();
        } else if (trimmedText.length > 0 && noteTextArea) {
            if (pendingNote) {
                removePendingDetailNote(parentId, pendingNote.id);
                document
                    .querySelector('#add-note-form')
                    ?.parentElement
                    ?.querySelector('.notes-list')
                    ?.querySelector(`[data-pending-note-id="${pendingNote.id}"]`)
                    ?.remove();
            }
            setDetailNoteDraft(parentId, submittedText);
            const activeNoteForm = document.querySelector(
                `#add-note-form[data-item-id="${parentId}"]`,
            ) || addNoteForm;
            noteTextArea = activeNoteForm.querySelector('textarea[name="note-text"]');
            if (!noteTextArea) {return;}
            noteTextArea.value = submittedText;
            noteTextArea.dispatchEvent(new Event('input', { bubbles: true }));
            syncNoteActions();
        }
    };

    addNoteForm.addEventListener('input', (e) => {
        if (e.target?.name !== 'note-text') {return;}
        noteTextArea = e.target;
        syncNoteActions();
    });

    if (cancelNoteBtn && noteTextArea) {
        cancelNoteBtn.onclick = () => {
            noteTextArea.value = '';
            noteTextArea.dispatchEvent(new Event('input', { bubbles: true }));
            clearDetailNoteDraft(parentId);
            syncNoteActions();
        };
    }

}
